/**
 * roleDashboard.js — Single source of truth for "which dashboard does
 * this user return to when an order flow is done?"
 *
 * Mirrors the `getDefaultRoute()` switch at App.js L1999. Keep the two
 * in sync — if a new persona is added, update both.
 *
 * This exists because imperative navigation after order completion
 * (Fulfillment, Tax Certificate, status changes) previously relied on
 * `navigate(-1)` or a hard-coded `/dashboard`, which produced the wrong
 * landing page for processors (who live on `/dashboard/processor-orders`)
 * and occasionally looped back into the just-closed order's Files tab
 * if that was the last entry in browser history.
 */

const ROLE_TO_DASHBOARD = {
  // Processors — live on /dashboard/processor-orders
  processor: '/dashboard/processor-orders',
  tax_processor: '/dashboard/processor-orders',
  title_search_processor: '/dashboard/processor-orders',
  search_processor: '/dashboard/processor-orders',

  // QC — dedicated dashboard
  qc_specialist: '/qc-dashboard',

  // Typing — dedicated queue (TYPING-001, Aug 2026)
  typing_processor: '/dashboard/typing',

  // Admin-adjacent personas with dedicated landing pages
  jurisdiction_manager: '/admin/jurisdictions/dashboard',
  jurisdiction_requestor: '/admin/my-jurisdiction-dashboard',
  accounting: '/admin/billing',

  // IT operations
  it_manager: '/admin/it-dashboard',
  it_support: '/admin/it-dashboard',

  // HR personas (HR-PERSONA-001, Jul 2026) — land on the Employee
  // Directory, never the operational order dashboard.
  hr_manager: '/admin/hr/dashboard',
  hr_specialist: '/admin/hr/dashboard',
  hr: '/admin/hr/employees',

  // CRM Phase 1
  sales_marketing: '/crm',
};

/**
 * Return the dashboard path a user should land on after finishing an
 * order flow. Accepts the user object (or just the role string); falls
 * back to the generic `/dashboard` for Admin / Team Leader / Order Desk.
 */
export function getRoleDashboardPath(userOrRole) {
  if (!userOrRole) return '/dashboard';
  const role = typeof userOrRole === 'string' ? userOrRole : userOrRole.role;
  if (!role) return '/dashboard';
  return ROLE_TO_DASHBOARD[role] || '/dashboard';
}
